import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { DateField } from '../../../../components/inputs/DateField';
import { MultiSelect } from '../../../../components/inputs/MultiSelect';
import { Select } from '../../../../components/inputs/Select';
import { Textarea } from '../../../../components/inputs/Textarea';
import { usePermission } from '../../../../auth/AuthContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { personnelApi, personsApi } from '../../../human-resources/personnel/personnelApi';
import { storageFacilitiesApi } from '../inventoryApi';
import { inventoryAuditsApi } from '../inventoryAuditApi';
import type { InventoryAuditDetail } from '../inventoryAuditTypes';
import { sampleInventoryAudit } from '../utils/sampleInventoryAudit';

/**
 * Raise a physical stock take — what will be counted, and when.
 *
 * <b>It captures no baseline, and the form says so.</b> The snapshot is taken when the count
 * actually begins, so an audit scheduled for next week measures against the stock that is there
 * on the day rather than against today's. Stating it here is what stops a reader raising one in
 * advance and believing the figures are already fixed.
 */
export function InventoryAuditModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: (audit: InventoryAuditDetail) => void;
}) {
  /*
    The auditor picker is WITHHELD without `personnel.read` rather than drawn and refused — hide,
    don't disable. A reader who lacks it raises an audit with nobody named, which is a real state:
    the field is optional. `inventory-audits` implies `personnel.read` SCOPED to create for
    exactly this picker, so a holder of the create key has it and a mere reader does not.
  */
  const canReadPersonnel = usePermission('personnel.read');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [auditedBy, setAuditedBy] = useState('');
  const [facilityIds, setFacilityIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');

  const [facilities, setFacilities] = useState<{ value: string; label: string }[]>([]);
  const [staff, setStaff] = useState<{ value: string; label: string }[]>([]);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open) return;

    const today = new Date();
    const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    // The window defaults to TODAY on both ends — a same-day count is the ordinary case, and a
    // reader scheduling one ahead moves both.
    setStartDate(iso);
    setEndDate(iso);
    setAuditedBy('');
    setFacilityIds([]);
    setNotes('');
    setConfirmed(false);
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();

    storageFacilitiesApi
      .options(controller.signal)
      .then((rows) => {
        if (controller.signal.aborted) return;
        setFacilities(rows.map((f) => ({ value: f.id, label: f.name })));
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [open]);

  /*
    The auditor picker resolves its NAMES through the identity service, in ONE batch call for the
    page of ids it is showing — never one request per row. That is the whole reason a personnel
    row carries a `personId` rather than a name.
  */
  useEffect(() => {
    if (!open || !canReadPersonnel) return;

    const controller = new AbortController();

    personnelApi
      .list({ pageSize: 100 }, controller.signal)
      .then(async (page) => {
        const names = await personsApi.resolve(
          // A record above this reader's access level arrives with NO personId — that absence
          // is the mask — so there is nothing to resolve. The option falls back to the employee
          // number below, which identifies the row without naming the person.
          page.items.map((p) => p.personId).filter((id): id is string => id != null),
          controller.signal,
        );

        if (controller.signal.aborted) return;

        const byPersonId = new Map(names.map((n) => [n.personId, n.displayName]));

        setStaff(
          page.items.map((p) => ({
            value: p.id,
            label: (p.personId != null ? byPersonId.get(p.personId) : undefined) ?? p.employeeNumber,
          })),
        );
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [open, canReadPersonnel]);

  const windowValid = startDate !== '' && endDate !== '' && endDate >= startDate;
  const valid = windowValid && facilityIds.length > 0;
  const canSave = valid && confirmed && !saving;

  const facilityLabels = useMemo(
    () =>
      facilities.filter((f) => facilityIds.includes(f.value)).map((f) => f.label).join(', '),
    [facilities, facilityIds],
  );

  function autogenerate() {
    const sample = sampleInventoryAudit(facilities.map((f) => f.value), staff.map((s) => s.value));

    setStartDate(sample.auditStartDate);
    setEndDate(sample.auditEndDate);
    setNotes(sample.notes);
    setFacilityIds(sample.storageFacilityIds);

    // Only where the picker is drawn at all — a generated id the reader cannot see would be a
    // value the form carries and cannot show.
    if (canReadPersonnel && sample.auditedByPersonnelId !== null) {
      setAuditedBy(sample.auditedByPersonnelId);
    }
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      onSaved(
        await inventoryAuditsApi.create({
          auditStartDate: startDate,
          auditEndDate: endDate,
          auditedByPersonnelId: auditedBy || null,
          notes: notes.trim() || null,
          storageFacilityIds: facilityIds,
        }),
      );
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not raise the audit.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'inventory-audit' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Raise inventory audit"
      size="lg"
      id="inventory-audit-modal"
      confirm={{
        id: 'inventory-audit-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      autogenerate={{ onGenerate: autogenerate, disabled: saving }}
      footer={
        <>
          <Button id="inventory-audit-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="inventory-audit-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <MultiSelect
          id="inventory-audit-storage-facility-ids"
          label="Storage facilities"
          description="Only these stores are counted. Pick more than one to cover several under a single audit — the sheet stays one row per item, with the per-store split behind each count."
          value={facilityIds}
          onChange={setFacilityIds}
          options={facilities}
          required
          disabled={saving}
          error={
            facilityIds.length > 0
              ? error?.fieldError('storageFacilityIds')
              : 'Choose at least one storage facility to count.'
          }
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <DateField
            id="inventory-audit-audit-start-date"
            label="Audit window opens"
            value={startDate}
            onChange={setStartDate}
            required
            disabled={saving}
            error={startDate === '' ? 'Choose the day the count window opens.' : error?.fieldError('auditStartDate')}
          />

          <DateField
            id="inventory-audit-audit-end-date"
            label="Audit window closes"
            value={endDate}
            onChange={setEndDate}
            min={startDate || undefined}
            required
            disabled={saving}
            error={
              endDate === ''
                ? 'Choose the day the count window closes.'
                : !windowValid
                  ? 'The window cannot end before it starts.'
                  : error?.fieldError('auditEndDate')
            }
          />
        </div>

        {/* Withheld entirely without the key — see the note at the top of this component. */}
        {canReadPersonnel ? (
          <Select
            id="inventory-audit-audited-by-personnel-id"
            label="Audit by"
            description="Who conducts the count. Optional — a sheet can be raised before the walker is decided."
            value={auditedBy}
            onChange={setAuditedBy}
            options={staff}
            disabled={saving}
            error={error?.fieldError('auditedByPersonnelId')}
          />
        ) : null}

        {/* FULL WIDTH, outside the grid — the remarks rule: it is the longest value on the form. */}
        <Textarea
          id="inventory-audit-notes"
          label="Notes"
          description="What this count covers, in your own words."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          minRows={2} maxRows={6}
          maxLength={1000}
          disabled={saving}
          error={error?.fieldError('notes')}
        />

        {/*
          The consequence, stated BEFORE the tick rather than appended to it. A reader who raises
          an audit in advance has to know the figures are not fixed yet, or they will read the
          sheet as already measured.
        */}
        <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          This records what will be counted and when. The on-hand baseline is captured when you
          press <b>Start count</b>, not now — so an audit scheduled for a later day still measures
          against the stock that is actually there on the day.
          {facilityLabels ? <> Counting {facilityLabels}.</> : null}
        </p>
      </div>
    </Modal>
  );
}
